import { useState } from 'react';
import { getTrendingProducts, getNewArrivals, products } from '@/mocks/products';
import ProductCard from '@/components/feature/ProductCard';

interface ProductRowProps {
  title: string;
  subtitle?: string;
  products: typeof products;
  link: string;
  linkLabel?: string;
}

function ProductRow({ title, subtitle, products: rowProducts, link, linkLabel = 'See All' }: ProductRowProps) {
  return (
    <div className="w-full bg-white rounded-lg overflow-hidden mb-4 md:mb-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-col">
          <h2 className="font-bold text-base md:text-lg text-foreground-950">
            {title}
          </h2>
          {subtitle && (
            <span className="text-xs text-foreground-500">{subtitle}</span>
          )}
        </div>
        <a
          href={link}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
        >
          {linkLabel}
          <i className="ri-arrow-right-s-line" />
        </a>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-0">
        {rowProducts.slice(0, 6).map((product, idx, arr) => (
          <div
            key={product.id}
            className={`border-r border-b border-gray-100 ${
              idx === arr.length - 1 ? 'last:border-r-0' : ''
            }`}
          >
            <ProductCard product={product} variant="compact" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProductShowcase() {
  const trending = getTrendingProducts().slice(0, 6);
  const recent = getNewArrivals().slice(0, 6);
  const beauty = products.filter((p) => p.categoryId === '5').slice(0, 6);
  const grocery = products.filter((p) => p.categoryId === '10').slice(0, 6);
  const phones = products.filter((p) => p.categoryId === '2').slice(0, 6);

  return (
    <section className="w-full py-4 md:py-6 px-4 md:px-6 lg:px-8">
      <div className="w-full">
        {/* Top Selling Items */}
        <ProductRow
          title="Top selling items"
          products={trending}
          link="/shop?sort=trending"
        />

        {/* Beauty Essentials */}
        <ProductRow
          title="Beauty Essentials"
          subtitle="Up To 70% Off"
          products={beauty}
          link="/shop?category=health-beauty"
        />

        {/* Grocery Essentials */}
        <ProductRow
          title="Grocery Essentials"
          subtitle="Free Pick-up Countrywide"
          products={grocery}
          link="/shop?category=supermarket"
        />

        {/* Phone Deals */}
        <ProductRow
          title="Phone Deals"
          products={phones}
          link="/shop?category=phones-tablets"
        />

        {/* Recent Finds */}
        <ProductRow
          title="Your Recent Finds"
          products={recent}
          link="/shop?sort=newest"
        />
      </div>
    </section>
  );
}